import { makeAutoObservable } from 'mobx'
import { EnumDropdownItemVariant } from 'shared/ui'
import { ICountriesEnum } from 'entities/Compliance'
import { ReachableUrlStore } from 'entities/Compliance/ui/ReachableUrl'
import type { InternationalStepsStore } from 'pages/settings/pages/compliance/pages/international/store/internationalStepsStore'
import {
  RegistrationAuthoritySelectStore,
  type RegistrationAuthorityConfig,
} from 'pages/settings/pages/compliance/ui/RegistrationAuthoritySelect'

const internationalRegistrationAuthorityConfig: RegistrationAuthorityConfig = {
  countries: [
    {
      label: 'UK: Company Registration Number (CRN)',
      id: ICountriesEnum.GB,
      iconL: 'flagUK',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'US: Employer Identification Number (EIN)',
      id: ICountriesEnum.US,
      iconL: 'flagUSA',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'CA: Canadian Business Number (CBN)',
      id: ICountriesEnum.CA,
      iconL: 'flagCanada',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'AU: Australian Company Number (ACN)',
      id: ICountriesEnum.AU,
      iconL: 'flagAU',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'Other',
      id: ICountriesEnum.OTHER,
      iconL: 'globe',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
  ],
}

export class BusinessInfoStore {
  private readonly _registrationAuthorityStore: RegistrationAuthoritySelectStore
  private _businessName: string | null = null
  private _einSearch = ''
  private _websiteUrl: string | null = null
  private _socialMediaUrl: string | null = null
  private _isCorrectUrl = false
  private _noWebsiteUrl = false
  private _needCheckUrlAfterChange = false
  private _einErrorMessage = ''

  readonly reachableWebsiteUrlStore = new ReachableUrlStore({
    errorText: 'Please enter a valid url (e.g. https://www.example.com/)',
    warningText:
      "Couldn't verify this link automatically. You can still continue — make sure it opens publicly.",
  })
  readonly reachableSocialUrlStore = new ReachableUrlStore({
    errorText: 'Please enter a valid url (e.g. https://www.example.com/)',
    warningText:
      "Couldn't verify this link automatically. You can still continue — make sure it opens publicly.",
  })

  constructor(private _internationalStepsStore: InternationalStepsStore) {
    this._registrationAuthorityStore = new RegistrationAuthoritySelectStore(
      internationalRegistrationAuthorityConfig
    )

    makeAutoObservable(this)
  }

  get einItem() {
    return this._internationalStepsStore.einItem
  }

  get businessName() {
    return this._businessName
  }

  get registrationAuthorityStore() {
    return this._registrationAuthorityStore
  }

  get websiteUrl() {
    return this._websiteUrl
  }

  get isCorrectUrl() {
    return this._isCorrectUrl
  }

  get hasWebsiteUrl() {
    return !!(
      this.websiteUrl?.length &&
      (this._isCorrectUrl || this.reachableWebsiteUrlStore.isWarning)
    )
  }

  get isUrlAccepted() {
    if (this._noWebsiteUrl) {
      return this._isCorrectUrl || this.reachableSocialUrlStore.isWarning
    }

    return this._isCorrectUrl || this.reachableWebsiteUrlStore.isWarning
  }

  get socialMediaUrl() {
    return this._socialMediaUrl
  }

  get noWebsiteUrl() {
    return this._noWebsiteUrl
  }

  get einFormatted() {
    const ein = this._einSearch.replace(/[^0-9]/g, '')

    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.US:
        return ein.length <= 2 ? this._einSearch : `${ein.slice(0, 2)}-${ein.slice(2)}`
      case ICountriesEnum.AU:
        return ein.match(/.{1,3}/g)?.join(' ')
      default:
        return ein
    }
  }

  get einOrigin() {
    return this._einSearch
  }

  get einPlaceholder() {
    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.US:
        return 'e.g. 12-3456789'
      case ICountriesEnum.CA:
        return 'e.g. 123456789'
      case ICountriesEnum.AU:
        return 'e.g. 123 456 789'
      default:
        return 'e.g. 12345678'
    }
  }

  get einLength() {
    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.GB:
        return 8
      case ICountriesEnum.US:
        return 9
      case ICountriesEnum.CA:
        return 9
      case ICountriesEnum.AU:
        return 9
      default:
        return null
    }
  }

  get selectedCountryKey() {
    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.GB:
        return 'UK:CRN'
      case ICountriesEnum.US:
        return 'US:EIN'
      case ICountriesEnum.CA:
        return 'CA:CBN'
      case ICountriesEnum.AU:
        return 'AU:ACN'
      case ICountriesEnum.OTHER:
        return 'OTHER'
      default:
        return null
    }
  }

  get isNextDisabled() {
    if (!this.registrationAuthorityStore.country) return true
    if (!this._businessName) return true
    if (!this.einOrigin) return true

    const hasValidUrl = this._websiteUrl || (this._noWebsiteUrl && this._socialMediaUrl)
    if (!hasValidUrl) return true

    return this.einLength && this.einOrigin.length !== this.einLength
  }

  get einErrorMessage() {
    return this._einErrorMessage
  }

  get einErrorMessageText() {
    return `Must be a ${this.einLength}-digit number ${this.einPlaceholder}`
  }

  init = () => {
    if (this.einItem) {
      this._businessName = this.einItem.business_name ?? null
      this._einSearch = this.einItem.ein?.replace(/[^0-9]/g, '') ?? ''
      this._websiteUrl = this.einItem?.website_url ?? null
      this._socialMediaUrl = this.einItem?.social_media_url ?? null

      if (this.einItem.country && !this._registrationAuthorityStore.country) {
        this._registrationAuthorityStore.setCountry(this.einItem.country)
      }
    }
  }

  setEinSearch = (search: string) => {
    const stringOrigin = search.replace(/[^0-9]/g, '')

    this.setEinErrorMessage('')

    if (this.einLength && stringOrigin.length > this.einLength) {
      this.setEinErrorMessage(this.einErrorMessageText)
    }

    this._einSearch = stringOrigin
  }

  checkEin = () => {
    if (this._einSearch.length && this.einLength && this._einSearch.length < this.einLength) {
      this.setEinErrorMessage(this.einErrorMessageText)
    }
  }

  setEinErrorMessage = (message: string) => {
    this._einErrorMessage = message
  }

  setIsCorrectUrl = (value: boolean) => {
    this._isCorrectUrl = value
  }

  setWebsiteUrl = (url: string | null, fromRequest?: boolean) => {
    this._needCheckUrlAfterChange = !fromRequest
    this.setIsCorrectUrl(false)

    this.reachableWebsiteUrlStore.dispose()

    if (fromRequest && url?.length) {
      this.setIsCorrectUrl(true)
    }
    const einItem = { ...this.einItem }

    einItem.website_url = url

    this._websiteUrl = url
    this._internationalStepsStore.updateEinItem(einItem)
  }

  setSocialMediaUrl = (url: string | null) => {
    this.reachableSocialUrlStore.dispose()

    const einItem = { ...this.einItem }

    einItem.social_media_url = url

    this._socialMediaUrl = url
    this._internationalStepsStore.updateEinItem(einItem)
  }

  checkUrl = async (url: string, type: 'websiteUrl' | 'socialMediaUrl') => {
    const result =
      type === 'websiteUrl'
        ? await this.reachableWebsiteUrlStore.checkSingleUrl(url)
        : await this.reachableSocialUrlStore.checkSingleUrl(url)

    if (!result.isSuccess && type === 'websiteUrl' && this._noWebsiteUrl) {
      this.reachableWebsiteUrlStore.dispose()
      return
    }

    if (result.isWarning) {
      this.setIsCorrectUrl(false)
      this._needCheckUrlAfterChange = false
      return
    }

    if (result.isError) {
      this.setIsCorrectUrl(false)
      this._needCheckUrlAfterChange = true
      return
    }

    this.setIsCorrectUrl(true)
    this._needCheckUrlAfterChange = false
  }

  setNoWebsiteUrl = (value: boolean) => {
    this._noWebsiteUrl = value
  }

  handleSetNoWebsiteUrl = () => {
    this.reachableWebsiteUrlStore.dispose()
    this.reachableSocialUrlStore.dispose()

    this.setWebsiteUrl(null)
    this.setSocialMediaUrl(null)
    this.setNoWebsiteUrl(!this._noWebsiteUrl)
  }

  addBusinessName = (business_name: string) => {
    const einItem = { ...this.einItem }

    einItem.business_name = business_name
    this._businessName = business_name

    this._internationalStepsStore.updateEinItem(einItem)
  }

  resetEinSearch = () => {
    this._einSearch = ''
    this.setEinErrorMessage('')
  }
}
